<template>
    <div
        v-bind="$attrs"
        class="ds-avatar"
        :class="`ds-avatar-size-${config.size}`"
        data-testid="ds-avatar"
    >
        <DsIcon
            v-if="config.type === 'icon'"
            data-testid="ds-avatar-type-icon"
            :icon="avatarContent"
            :size="config.size"
        />
        <img
            v-else-if="config.type === 'bank'"
            alt=""
            class="h-auto"
            data-testid="ds-avatar-type-bank"
            :src="config.logoUrl"
        />
        <span
            v-else
            class="uppercase"
            data-testid="ds-avatar-type-initials"
        >
            {{ avatarContent }}
        </span>
    </div>
</template>

<script setup lang="ts">
import { getInitials } from '@/shared/utils/strings';
import type { IconDefinition } from '@fortawesome/fontawesome-svg-core';
import { computed } from 'vue';
import DsIcon from '../DsIcon.vue';

export type DsAvatarSize = 'sm' | 'md' | 'lg' | 'xl';
export type DsAvatarType = 'initials' | 'icon' | 'bank';

type CommonAvatarProps = {
    size: DsAvatarSize;
};

export type InitialsAvatarProps = CommonAvatarProps & {
    type: 'initials';
    firstName: string;
    lastName?: string;
};

export type IconAvatarProps = CommonAvatarProps & {
    type: 'icon';
    icon: string | string[] | IconDefinition;
};

export type BankAvatarProps = CommonAvatarProps & {
    type: 'bank';
    logoUrl: string;
};

export type DsAvatarProps = {
    config: InitialsAvatarProps | IconAvatarProps | BankAvatarProps;
};

const props = defineProps<DsAvatarProps>();

const avatarContent = computed(() => {
    if (props.config.type === 'initials') {
        if (props.config.size === 'sm') {
            return getInitials(props.config.firstName);
        }

        return getInitials(props.config.firstName, props.config.lastName);
    } else if (props.config.type === 'icon') {
        return props.config.icon;
    }

    return '';
});
</script>

<style scoped lang="scss">
.ds-avatar {
    @apply flex aspect-square flex-shrink-0 items-center justify-center rounded-full bg-ds-primary-200 text-content-secondary;
}

.ds-avatar-size- {
    &sm {
        @apply size-6 text-content-sm;

        svg {
            @apply w-2;
        }
    }
    &md {
        @apply size-8 text-content-md;

        svg {
            @apply w-3;
        }
    }
    &lg {
        @apply size-12 text-content-lg;

        svg {
            @apply w-5;
        }
    }
    &xl {
        @apply size-14 text-heading-sm font-semibold;

        svg {
            @apply w-6;
        }
    }
}
</style>
